import { Suspense, lazy, useMemo } from 'react';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import {
  apiDocsConfigRef,
  defaultDefinitionWidgets,
} from '@backstage/plugin-api-docs';
import { ApiBlueprint } from '@backstage/frontend-plugin-api';
import type { FrontendPlugin } from '@backstage/frontend-plugin-api';
import { Progress } from '@backstage/core-components';
import { useTheme } from '@mui/material/styles';
import type { ApiEntity } from '@backstage/catalog-model';

const FALLBACK_SECURITY_SCHEMES = {
  httpBasic: {
    type: 'http',
    scheme: 'basic',
  },
  httpBearer: {
    type: 'http',
    scheme: 'bearer',
  },
  apiKeyHeader: {
    type: 'apiKey',
    in: 'header',
    name: 'X-API-Key',
  },
} as const;

const addFallbackSecuritySchemes = (definition: string): string => {
  try {
    const document = JSON.parse(definition) as Record<string, unknown>;
    const components = (document.components ?? {}) as Record<string, unknown>;
    const securitySchemes = components.securitySchemes as
      | Record<string, unknown>
      | undefined;

    if (securitySchemes && Object.keys(securitySchemes).length > 0) {
      return definition;
    }

    return JSON.stringify({
      ...document,
      components: {
        ...components,
        securitySchemes: FALLBACK_SECURITY_SCHEMES,
      },
    });
  } catch {
    return definition;
  }
};

const LazyApiReferenceReact = lazy(() =>
  Promise.all([
    import('@scalar/api-reference-react'),
    import('@scalar/api-reference-react/style.css'),
  ]).then(([mod]) => ({ default: mod.ApiReferenceReact })),
);

const ScalarOpenApiRenderer = ({ definition }: { definition: string }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const configuration = useMemo(
    () => ({
      content: addFallbackSecuritySchemes(definition),
      hideDownloadButton: true,
      hideDarkModeToggle: false,
      layout: 'modern' as const,
      defaultOpenAllTags: true,
      forceDarkModeState: isDark ? ('dark' as const) : ('light' as const),
      showDeveloperTools: 'localhost' as const,
      agent: {
        disabled: true,
      },
      authentication: {
        createAnySecurityScheme: true,
      },
    }),
    [definition, isDark],
  );

  return (
    <Suspense fallback={<Progress />}>
      <LazyApiReferenceReact configuration={configuration} />
    </Suspense>
  );
};

const scalarOpenApiWidget = {
  type: 'openapi',
  title: 'OpenAPI',
  component: (definition: string) => (
    <ScalarOpenApiRenderer definition={definition} />
  ),
};

const scalarApiDocsConfigExtension = ApiBlueprint.make({
  name: 'config',
  params: defineParams =>
    defineParams({
      api: apiDocsConfigRef,
      deps: {},
      factory: () => {
        const widgets = defaultDefinitionWidgets().map(w =>
          w.type === 'openapi' ? scalarOpenApiWidget : w,
        );
        return {
          getApiDefinitionWidget: (apiEntity: ApiEntity) =>
            widgets.find(d => d.type === apiEntity.spec.type),
        };
      },
    }),
});

export const apiDocsPluginOverride: FrontendPlugin =
  apiDocsPlugin.withOverrides({
    extensions: [scalarApiDocsConfigExtension],
  });
